import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Target, 
  Users, 
  Award, 
  Heart, 
  Globe, 
  Zap,
  CheckCircle,
  TrendingUp
} from 'lucide-react';

const AboutUsPage = () => {
  const values = [
    {
      icon: <Target className="h-6 w-6 text-primary" />,
      title: "Excellence",
      description: "We strive for excellence in every feature and interaction, ensuring the best possible experience for cricket enthusiasts."
    },
    {
      icon: <Users className="h-6 w-6 text-primary" />,
      title: "Community",
      description: "Building a strong cricket community by connecting players, teams, and tournaments worldwide."
    },
    {
      icon: <Heart className="h-6 w-6 text-primary" />,
      title: "Passion",
      description: "Our passion for cricket drives us to create innovative solutions that enhance the sport we love."
    },
    {
      icon: <Globe className="h-6 w-6 text-primary" />,
      title: "Innovation",
      description: "Continuously innovating to bring cutting-edge technology to cricket scoring and management."
    }
  ];

  const milestones = [
    {
      year: "2024",
      title: "Platform Launch",
      description: "Successfully launched our comprehensive cricket scoring platform with live match tracking."
    },
    {
      year: "2024",
      title: "100+ Tournaments",
      description: "Supported over 100 cricket tournaments across different formats and levels."
    },
    {
      year: "2024",
      title: "Advanced Analytics",
      description: "Introduced advanced analytics and performance tracking features."
    },
    {
      year: "2024",
      title: "Mobile App",
      description: "Launched mobile-responsive design for seamless scoring on any device."
    }
  ];

  const team = [
    {
      name: "Cricket Enthusiasts",
      role: "Development Team",
      description: "A dedicated team of cricket lovers and technology experts committed to revolutionizing cricket scoring."
    },
    {
      name: "Sports Analysts",
      role: "Advisory Board",
      description: "Experienced cricket analysts and former players providing insights and guidance."
    },
    {
      name: "Technology Experts",
      role: "Engineering Team",
      description: "Skilled developers and designers creating intuitive and powerful cricket management tools."
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-foreground mb-6">
            About Cricket Scoring
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We're passionate about cricket and technology, bringing you the most comprehensive and user-friendly cricket scoring platform designed for tournaments, teams, and cricket enthusiasts worldwide.
          </p>
        </div>

        {/* Mission Section */}
        <Card className="mb-16 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
          <CardContent className="pt-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-4">Our Mission</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                To revolutionize cricket scoring and tournament management by providing innovative, reliable, and user-friendly tools that enhance the cricket experience for players, teams, and organizers.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  What We Do
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Provide comprehensive cricket scoring solutions</li>
                  <li>• Manage tournaments and team competitions</li>
                  <li>• Track player and team performance</li>
                  <li>• Generate detailed statistics and reports</li>
                  <li>• Offer real-time live scoring capabilities</li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Our Vision
                </h3>
                <p className="text-muted-foreground">
                  To become the global standard for cricket scoring and tournament management, empowering cricket communities worldwide with cutting-edge technology and exceptional user experiences.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Values Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    {value.icon}
                  </div>
                  <CardTitle className="text-lg">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm">
                    {value.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Milestones Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">Our Journey</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {milestones.map((milestone, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{milestone.title}</CardTitle>
                    <Badge variant="outline">{milestone.year}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {milestone.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-foreground text-center mb-12">Our Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {team.map((member, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{member.name}</CardTitle>
                  <Badge variant="secondary">{member.role}</Badge>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm">
                    {member.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
          <CardContent className="pt-8 text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              Join Our Cricket Community
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Whether you're a tournament organizer, team captain, or cricket enthusiast, we're here to help you take your cricket experience to the next level.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors">
                Get Started Today
              </button>
              <button className="border border-border px-6 py-3 rounded-lg font-semibold hover:bg-accent transition-colors">
                Contact Us
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AboutUsPage; 